/**
 * mutationError — shared, pure error-message normalization for RolesUsers-V2
 * mutations. Treats server 401/403 as authoritative with clear, user-facing copy
 * (defense-in-depth: the client may show an action, but the server is truth).
 *
 * Works on BOTH raw errors (from direct `RolesUsersApi` calls — carry
 * `err.response.status`/`err.response.data.error`) and pre-normalized strings
 * (from a thunk's `rejectWithValue`) — so it's safe to call at either layer and
 * is idempotent on an already-friendly string.
 *
 * @module mutationError
 */

/**
 * Best-effort HTTP status for an error, from `err.response.status` or an
 * `"HTTP 403: …"` message string (BaseApi's `apiRequest` throws that shape).
 *
 * @param {*} err
 * @returns {number|null}
 */
export function getErrorStatus(err) {
	if (err && err.response && Number.isFinite(Number(err.response.status))) {
		return Number(err.response.status);
	}
	const message = typeof err === 'string' ? err : err?.message || '';
	const match = /\bHTTP\s+(\d{3})\b/.exec(message);
	return match ? Number(match[1]) : null;
}

/**
 * The backend's human message, if any (`err.response.data.error` — string or
 * array).
 *
 * @param {*} err
 * @returns {string|null}
 */
export function extractBackendMessage(err) {
	const backend = err?.response?.data?.error;
	if (Array.isArray(backend)) return backend.length ? String(backend[0]) : null;
	if (typeof backend === 'string' && backend.trim().length > 0) return backend;
	return null;
}

/**
 * A clear, user-facing message for a failed mutation.
 *
 * - 401 → session expired (authoritative; refresh/sign-in).
 * - 403 → the backend's specific reason if present (e.g. "You cannot remove the
 *   only Owner"), else a generic no-permission message.
 * - otherwise → the backend message, then a string error, then `err.message`,
 *   then the fallback.
 *
 * @param {*} err
 * @param {string} [fallback]
 * @returns {string}
 */
export function friendlyMutationError(err, fallback = 'Action failed. Please try again.') {
	const status = getErrorStatus(err);
	const backend = extractBackendMessage(err);

	if (status === 401) {
		return 'Your session has expired. Please refresh the page and sign in again.';
	}
	if (status === 403) {
		return backend || 'You don’t have permission to perform this action.';
	}
	if (backend) return backend;
	if (typeof err === 'string' && err.length > 0) return err;
	return err?.message || fallback;
}
